/**
 * 소요 오리피스 면적 → API 526 표준 오리피스 선정.
 *
 * 근거: API Standard 520 Part I, 10판(2020)
 *   - 5.6.3.2.4  "See API 526 for selection of the proper orifice size."
 *   - 5.6.4.2.4, 5.7.2.4  동일 취지
 *   표준은 "계산된 소요 면적 이상인 표준 오리피스를 고른다"는 원칙만 제시하며,
 *   본 모듈은 그중 **가장 작은 것**(= 과대설계를 피하는 선택)을 반환한다.
 *
 * 이 모듈은 순수 함수만 포함한다. React·DOM·전역 상태를 import 하지 말 것 —
 * UI 없이 단독으로 테스트가 돌아가야 한다(CLAUDE.md).
 */
import {
  API_526_ORIFICES,
  LARGEST_ORIFICE,
  type Api526Orifice,
} from "../data/api526";

/** 오리피스 선정 결과(성공). */
export interface OrificeSelection {
  readonly orifice: Api526Orifice;
  /** 입력된 소요 면적 (mm²) */
  readonly requiredAreaMm2: number;
  /**
   * 여유율 (%) = (선정면적 − 소요면적) / 소요면적 × 100.
   * 표준 오리피스가 이산적이라 항상 0 이상이며, 값이 너무 크면
   * 과대설계(oversizing)로 채터링 위험이 있어 설계자 판단이 필요하다.
   */
  readonly marginPct: number;
  /** 병렬 설치 개수. 1개로 충분하면 1. */
  readonly valveCount: number;
  /** 밸브 1개가 담당하는 소요 면적 (mm²) */
  readonly areaPerValveMm2: number;
}

/** 오리피스 선정 실패 사유. */
export type OrificeSelectionFailure =
  | {
      readonly ok: false;
      readonly reason: "invalid-input";
      readonly message: string;
    }
  | {
      readonly ok: false;
      readonly reason: "exceeds-largest";
      readonly message: string;
      readonly requiredAreaMm2: number;
      /** 가장 큰 오리피스(T) 하나로 감당 가능한 면적 */
      readonly largestAreaMm2: number;
      /** 병렬 설치가 필요한 최소 개수 */
      readonly minimumValveCount: number;
    };

export type OrificeSelectionResult =
  | ({ readonly ok: true } & OrificeSelection)
  | OrificeSelectionFailure;

/** 병렬 설치 시 최대 허용 개수. 이보다 많이 필요하면 설계 재검토 대상. */
const MAX_VALVE_COUNT = 8;

/**
 * 소요 면적에 맞는 최소 표준 오리피스를 선정한다.
 *
 * @param requiredAreaMm2 API 520 사이징식으로 구한 소요 유효 오리피스 면적 (mm²)
 * @param valveCount      병렬 설치 개수(기본 1). 2 이상이면 소요 면적을 균등 분담한다.
 */
export function selectOrifice(
  requiredAreaMm2: number,
  valveCount = 1,
): OrificeSelectionResult {
  if (!Number.isFinite(requiredAreaMm2) || requiredAreaMm2 <= 0) {
    return {
      ok: false,
      reason: "invalid-input",
      message: "소요 면적은 0보다 큰 유한한 값이어야 합니다.",
    };
  }
  if (!Number.isInteger(valveCount) || valveCount < 1) {
    return {
      ok: false,
      reason: "invalid-input",
      message: "밸브 개수는 1 이상의 정수여야 합니다.",
    };
  }

  const areaPerValveMm2 = requiredAreaMm2 / valveCount;

  const orifice = API_526_ORIFICES.find(
    (o) => o.effectiveAreaMm2 >= areaPerValveMm2,
  );

  if (!orifice) {
    const largestAreaMm2 = LARGEST_ORIFICE.effectiveAreaMm2;
    return {
      ok: false,
      reason: "exceeds-largest",
      message:
        `소요 면적 ${requiredAreaMm2.toFixed(0)} mm² 는 표준 최대 오리피스` +
        ` ${LARGEST_ORIFICE.designation}(${largestAreaMm2.toFixed(0)} mm²)` +
        ` ${valveCount}개로 감당할 수 없습니다. 병렬 설치 개수를 늘리거나 설계를 재검토하십시오.`,
      requiredAreaMm2,
      largestAreaMm2,
      minimumValveCount: Math.ceil(requiredAreaMm2 / largestAreaMm2),
    };
  }

  return {
    ok: true,
    orifice,
    requiredAreaMm2,
    areaPerValveMm2,
    valveCount,
    marginPct:
      ((orifice.effectiveAreaMm2 - areaPerValveMm2) / areaPerValveMm2) * 100,
  };
}

/**
 * 표준 오리피스 1개로 부족할 때, 감당 가능한 최소 병렬 개수로 선정한다.
 * 개수가 MAX_VALVE_COUNT 를 넘으면 실패를 반환한다.
 */
export function selectOrificeWithParallel(
  requiredAreaMm2: number,
): OrificeSelectionResult {
  for (let n = 1; n <= MAX_VALVE_COUNT; n += 1) {
    const result = selectOrifice(requiredAreaMm2, n);
    if (result.ok) return result;
    if (result.reason === "invalid-input") return result;
  }
  return selectOrifice(requiredAreaMm2, MAX_VALVE_COUNT);
}
